<template>
  <SoDialog
    v-model="value"
    :max-width="600"
    card-title="Are you sure?"
    data-testid="contact-remove-modal"
  >
    <SoAlert
      v-if="isProtectedContact"
      type="warning"
      data-testid="contact-remove-modal--protected-message"
      title="This contact cannot be deleted directly. Use the Manage bill delivery methods modal in the sidebar (or Priority Services for PSR-managed paper bills) to remove it."
    />
    <SoAlert
      v-else-if="contact?.juniferPrimaryContact"
      type="warning"
      title="Removing the contact will cancel the account contact effective from today."
    />
    <SoAlert v-else-if="submitError" type="error" :title="submitError" />

    <div class="d-flex justify-end mt-8">
      <SoButton title="cancel" class="mr-2" large @click="closeModal()" />
      <SoButton
        v-if="!isProtectedContact"
        title="yes"
        large
        @click="removeContact()"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { IContact } from "@/models/contacts/contact.types"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { errorReporter } from "@/services/ErrorService"
  import { useSnackbarStore } from "@/stores/snackbar"

  export default defineComponent({
    components: {
      SoDialog,
      SoButton,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      juniferContactId: {
        type: Number,
        default: undefined,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        removeContactMutator: useCurrentAccountStore().removeContact(),
        snackbar: useSnackbarStore(),
        submitError: "",
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      contact() {
        return this.current.accountContacts.data.find(
          (contact: IContact) =>
            contact.juniferContactId === this.juniferContactId,
        )
      },
      isProtectedContact(): boolean {
        const reservedEmails =
          this.current.paperBillState.data?.reservedPaperBillContactEmails ?? []
        return (
          !!this.contact?.email && reservedEmails.includes(this.contact.email)
        )
      },
    },
    methods: {
      removeContact() {
        if (!this.contact) throw Error("Contact is not specified")
        this.submitError = ""

        /*
          The contact as read is passed whole: cancelling goes through
          updateAccountContact, which requires the surname and all three comms
          preferences alongside cancelled: true.
        */
        this.removeContactMutator
          .mutateAsync({ contact: this.contact })
          .then(() => {
            this.snackbar.addSuccess(
              "Successfully deleted customer contact details. It may take some time for the changes to take effect",
            )
            this.closeModal()
          })
          .catch((error: unknown) => {
            /*
              A gateway Error union member makes the mutator throw, so show the
              reason rather than letting the rejection reach SoErrorBoundary.
            */
            this.submitError =
              error instanceof Error
                ? error.message
                : "Failed to remove contact."
            errorReporter.report(error)
          })
      },
      closeModal() {
        this.submitError = ""
        this.removeContactMutator.reset()
        this.value = false
      },
    },
  })
</script>

<style scoped>
  .contact-details__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .contact-details__inline-input {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 8px;
    margin: 4px 0px;
    letter-spacing: normal;
  }
</style>
